import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from './translations';

const LanguageContext = createContext(null);

const LANG_STORAGE_KEY = 'edelnad-lang';
const REDIRECT_SESSION_KEY = 'edelnad-redirected';
const BOT_RE = /bot|crawl|spider|slurp|baidu|bingpreview|facebookexternalhit|twitterbot|linkedinbot|telegrambot|whatsapp|preview|headless/i;

// Derive the language from a pathname: anything under /de is German, else English.
export function langFromPath(pathname) {
  return /^\/de(\b|\/|$)/.test(pathname) ? 'de' : 'en';
}

// Return the path with the language prefix removed ('/de' or '').
function stripLang(pathname) {
  if (pathname === '/de') return '/';
  if (pathname.startsWith('/de/')) return pathname.slice(3); // keep leading slash
  return pathname;
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search, hash } = location;
  const lang = langFromPath(pathname);
  const langPrefix = lang === 'de' ? '/de' : '';

  // Keep the latest lang in a ref so the one-time detection effect doesn't
  // need to re-run on every navigation.
  const didDetect = useRef(false);

  // One-time browser-language redirect for first-time German visitors.
  useEffect(() => {
    if (didDetect.current) return;
    didDetect.current = true;

    if (typeof window === 'undefined') return;

    // Already on a German path — nothing to do.
    if (langFromPath(window.location.pathname) === 'de') return;

    // Prerender pass — must not redirect, or we'd capture the wrong page.
    if (window.__PRERENDER__) return;

    // Respect an explicit prior choice.
    if (localStorage.getItem(LANG_STORAGE_KEY)) return;

    // Avoid a redirect loop if we already sent someone to /de this session.
    if (sessionStorage.getItem(REDIRECT_SESSION_KEY)) return;

    // Don't redirect bots — they should see the URL they requested.
    const ua = navigator.userAgent || '';
    if (BOT_RE.test(ua)) return;

    const langs = navigator.languages || [];
    const primary = langs[0] || navigator.language || '';
    if (!/^de/i.test(primary)) return;

    sessionStorage.setItem(REDIRECT_SESSION_KEY, '1');
    localStorage.setItem(LANG_STORAGE_KEY, 'de');
    const target = '/de' + window.location.pathname + window.location.search + window.location.hash;
    navigate(target, { replace: true });
  }, [navigate]);

  // tr(en, de) — inline one-off copy.
  const tr = useCallback((en, de) => (lang === 'de' ? (de ?? en) : en), [lang]);

  // t(key) — dictionary lookup with de -> en -> key fallback.
  const t = useCallback(
    (key) => {
      const entry = translations[key];
      if (!entry) return key;
      if (lang === 'de') return entry.de ?? entry.en ?? key;
      return entry.en ?? key;
    },
    [lang],
  );

  // buildPath(to) — prefix an in-app route with /de when in German.
  // to='/' -> '/de' in German, '/' in English.
  // to='/work/foo' -> '/de/work/foo' in German.
  const buildPath = useCallback(
    (to) => {
      if (lang !== 'de') return to;
      if (!to) return to;
      if (to.startsWith('#')) return to; // in-page anchors stay as-is
      if (to === '/') return '/de';
      if (to.startsWith('/')) return `/de${to}`;
      return to;
    },
    [lang],
  );

  // switchLang(target) — navigate (replace) to the equivalent path in the
  // other language, preserving hash + query. Persists the explicit choice so
  // detection never overrides it again.
  const switchLang = useCallback(
    (target) => {
      const nextLang = target; // 'en' | 'de'
      localStorage.setItem(LANG_STORAGE_KEY, nextLang);
      const base = stripLang(pathname); // language-agnostic path
      const prefix = nextLang === 'de' ? '/de' : '';
      const next = `${prefix}${base === '/' ? '' : base}${search}${hash}`;
      navigate(next, { replace: true });
    },
    [pathname, search, hash, navigate],
  );

  // The path of the *other* language for the current URL (used by LangToggle
  // to render a real, crawlable <Link>).
  const otherLangPath = useMemo(() => {
    const base = stripLang(pathname);
    const other = lang === 'de' ? '' : '/de';
    return `${other}${base === '/' ? '' : base}${search}${hash}` || (other === '/de' ? '/de' : '/');
  }, [pathname, search, hash, lang]);

  const value = useMemo(
    () => ({ lang, langPrefix, tr, t, buildPath, switchLang, otherLangPath }),
    [lang, langPrefix, tr, t, buildPath, switchLang, otherLangPath],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLang must be used within a LanguageProvider');
  }
  return ctx;
}

// ── Portfolio item localization ─────────────────────────────────────────────
// Merge German overrides onto an English item. Falls back to English for any
// field that isn't translated, so partial translation never breaks rendering.
export function localizeItem(item, lang, deMap) {
  if (!item) return item;
  if (lang !== 'de' || !deMap) return item;
  const de = deMap[item.slug];
  if (!de) return item;

  const merged = { ...item };
  if (de.title) merged.title = de.title;
  if (de.badge) merged.badge = de.badge;

  if (de.campaign || item.campaign) {
    const enC = item.campaign || {};
    const deC = de.campaign || {};
    merged.campaign = {
      ...enC,
      ...deC,
      products: (enC.products || []).map((product, pi) => {
        const deProduct = deC.products?.[pi];
        if (!deProduct) return product;
        return {
          ...product,
          name: deProduct.name ?? product.name,
          shoots: (product.shoots || []).map((shoot, si) => {
            const deShoot = deProduct.shoots?.[si];
            if (!deShoot) return shoot;
            return { ...shoot, ...deShoot };
          }),
        };
      }),
    };
  }

  return merged;
}
